import {
    type ViewProps,
    Platform
} from "react-native";
import type {
    DataTableProcessConstantType,
    DataTableSortConstantType,
    DataTableCardLayoutType,
    DataTableColumnFilters,
    DataTableSortDirection,
    IDataTableColumnBase,
    DataTableExportData,
    IDataTableSortRule,
    IDataTableColumn,
    DataTableValue
} from "./type";
import type {
    TableSortDirection
} from "../table/type";
import {
    getIntlLocale,
    formatNumber
} from "../../helpers/locale";
import {
    warnOnce
} from "../../utils/development";

const DATA_TABLE_COLLATORS = new Map<string, Intl.Collator>();

const getDataTableCollator = (intlLocale: string): Intl.Collator => {
    const cached = DATA_TABLE_COLLATORS.get(intlLocale);

    if(cached) {
        return cached;
    }

    const collator = new Intl.Collator(intlLocale, {
        numeric: true
    });

    DATA_TABLE_COLLATORS.set(intlLocale, collator);

    return collator;
};

export type DataTableProcessedRow<T> = {
    index: number;
    row: T;
};

export const getDataTableValueText = (value: DataTableValue): string => {
    if(value === null || value === undefined) {
        return "";
    }

    if(value instanceof Date) {
        return value.toISOString();
    }

    return String(value);
};

const getDataTableValueNumber = (value: DataTableValue): number | undefined => {
    if(typeof value === "number") {
        return value;
    }

    if(value instanceof Date) {
        return value.getTime();
    }

    if(typeof value === "boolean") {
        return Number(value);
    }

    return undefined;
};

export const compareDataTableValues = ({
    collator,
    second,
    first
}: {
    collator: Intl.Collator;
    second: DataTableValue;
    first: DataTableValue;
}): number => {
    const isFirstEmpty = first === null || first === undefined;

    const isSecondEmpty = second === null || second === undefined;

    if(isFirstEmpty || isSecondEmpty) {
        return Number(isFirstEmpty) - Number(isSecondEmpty);
    }

    const firstNumber = getDataTableValueNumber(first);

    const secondNumber = getDataTableValueNumber(second);

    if(firstNumber !== undefined && secondNumber !== undefined) {
        return firstNumber - secondNumber;
    }

    return collator.compare(getDataTableValueText(first), getDataTableValueText(second));
};

export const getDataTableRowMatcher = <T, >({
    excludedColumnId,
    columnFilters,
    searchColumns,
    globalFilter,
    intlLocale,
    columns,
    locale
}: {
    searchColumns?: Array<IDataTableColumn<T>>;
    columnFilters: DataTableColumnFilters;
    columns: Array<IDataTableColumn<T>>;
    excludedColumnId?: string;
    globalFilter: string;
    intlLocale: string;
    locale?: string;
}) => {
    const query = globalFilter.trim().toLocaleLowerCase(intlLocale);

    const activeFilters = columns.filter((column) => column.id !== excludedColumnId && column.accessor && (columnFilters[column.id]?.length ?? 0) > 0);

    return (row: T): boolean => {
        const isQueryMatched = !query || (searchColumns ?? columns).some((column) => {
            if(!column.accessor) {
                return false;
            }

            const value = column.accessor(row);

            if(getDataTableValueText(value).toLocaleLowerCase(intlLocale).includes(query)) {
                return true;
            }

            if(typeof value === "number" && locale) {
                return formatNumber({
                    locale,
                    value
                }).toLocaleLowerCase(intlLocale).includes(query);
            }

            if(value instanceof Date && !Number.isNaN(value.getTime())) {
                return value.toLocaleDateString(intlLocale).toLocaleLowerCase(intlLocale).includes(query);
            }

            return false;
        });

        if(!isQueryMatched) {
            return false;
        }

        return activeFilters.every((column) => columnFilters[column.id]?.includes(getDataTableValueText(column.accessor?.(row))));
    };
};

export const processDataTableRows = <T, >({
    isManualFiltering,
    isManualSorting,
    columnFilters,
    searchColumns,
    globalFilter,
    columns,
    sorting,
    locale,
    data
}: DataTableProcessConstantType<T>): Array<DataTableProcessedRow<T>> => {
    const intlLocale = getIntlLocale({
        locale
    });

    const processed = data.map((row, index) => ({
        index,
        row
    }));

    const matcher = getDataTableRowMatcher({
        columnFilters,
        searchColumns,
        globalFilter,
        intlLocale,
        columns,
        locale
    });

    const filtered = isManualFiltering ? processed : processed.filter((item) => matcher(item.row));

    if(isManualSorting || !sorting.length) {
        return filtered;
    }

    const collator = getDataTableCollator(intlLocale);

    const sortColumns = sorting.map((rule) => ({
        column: columns.find((item) => item.id === rule.id),
        direction: rule.direction
    }));

    return [
        ...filtered
    ].sort((first, second) => {
        for(const {
            direction,
            column
        } of sortColumns) {
            if(!column?.accessor) {
                continue;
            }

            const firstValue = column.accessor(first.row);

            const secondValue = column.accessor(second.row);

            const isFirstEmpty = firstValue === null || firstValue === undefined;

            const isSecondEmpty = secondValue === null || secondValue === undefined;

            if(isFirstEmpty !== isSecondEmpty) {
                return Number(isFirstEmpty) - Number(isSecondEmpty);
            }

            const result = compareDataTableValues({
                second: secondValue,
                first: firstValue,
                collator
            });

            if(result !== 0) {
                return direction === "asc" ? result : -result;
            }
        }

        return first.index - second.index;
    });
};

export const getDataTableFacetCounts = <T, >({
    isManualFiltering,
    columnFilters,
    searchColumns,
    globalFilter,
    columns,
    locale,
    data
}: {
    searchColumns?: Array<IDataTableColumn<T>>;
    columnFilters: DataTableColumnFilters;
    columns: Array<IDataTableColumn<T>>;
    isManualFiltering: boolean;
    globalFilter: string;
    locale: string;
    data: Array<T>;
}): Record<string, Record<string, number>> => {
    const intlLocale = getIntlLocale({
        locale
    });

    const counts: Record<string, Record<string, number>> = {};

    columns.forEach((column) => {
        if(!column.filterOptions || !column.accessor) {
            return;
        }

        const columnCounts: Record<string, number> = {};

        if(isManualFiltering) {
            counts[column.id] = columnCounts;

            return;
        }

        const matcher = getDataTableRowMatcher({
            excludedColumnId: column.id,
            columnFilters,
            searchColumns,
            globalFilter,
            intlLocale,
            columns,
            locale
        });

        data.forEach((row) => {
            if(!matcher(row)) {
                return;
            }

            const value = getDataTableValueText(column.accessor?.(row));

            columnCounts[value] = (columnCounts[value] ?? 0) + 1;
        });

        counts[column.id] = columnCounts;
    });

    return counts;
};

const DATA_TABLE_CSV_BOM = String.fromCharCode(0xFEFF);

const getDataTableCsvCell = (value: string): string => {
    const isDangerous = /^[=+\-@\t\r]/.test(value) && Number.isNaN(Number(value));

    const safeValue = isDangerous ? `'${value}` : value;

    if(!/[",\n\r]/.test(safeValue)) {
        return safeValue;
    }

    return `"${safeValue.replace(/"/g, "\"\"")}"`;
};

export const getDataTableCsv = ({
    columns,
    rows
}: DataTableExportData): string => {
    const lines = [
        columns.map((column) => getDataTableCsvCell(column.header)),
        ...rows.map((row) => row.map(getDataTableCsvCell))
    ];

    return lines.map((line) => line.join(",")).join("\r\n");
};

export const getDataTableCellContent = <T, >({
    column,
    locale,
    index,
    row
}: {
    column: IDataTableColumn<T>;
    locale: string;
    index: number;
    row: T;
}) => {
    if(column.cell) {
        return column.cell(row, index);
    }

    const value = column.accessor?.(row);

    if(typeof value === "number") {
        return formatNumber({
            locale,
            value
        });
    }

    return getDataTableValueText(value);
};

export const getDataTableNextSorting = ({
    sortFirst = "asc",
    isAdditive,
    behaviour,
    columnId,
    sorting
}: DataTableSortConstantType): Array<IDataTableSortRule> => {
    if(behaviour === "none") {
        return sorting;
    }

    const currentRule = sorting.find((rule) => rule.id === columnId);

    const oppositeDirection = sortFirst === "asc" ? "desc" : "asc";

    let nextDirection: IDataTableSortRule["direction"] | undefined = sortFirst;

    if(currentRule?.direction === sortFirst) {
        nextDirection = oppositeDirection;
    } else if(currentRule) {
        nextDirection = undefined;
    }

    if(behaviour === "multiple" && isAdditive) {
        const others = sorting.filter((rule) => rule.id !== columnId);

        if(!nextDirection) {
            return others;
        }

        if(!currentRule) {
            return [
                ...others,
                {
                    direction: nextDirection,
                    id: columnId
                }
            ];
        }

        return sorting.map((rule) => rule.id === columnId ? {
            ...rule,
            direction: nextDirection
        } : rule);
    }

    return nextDirection ? [
        {
            direction: nextDirection,
            id: columnId
        }
    ] : [];
};

export const getDataTableIsShiftPressed = (event: unknown): boolean => {
    return typeof event === "object" && event !== null && "shiftKey" in event && event.shiftKey === true;
};

export const getDataTableAriaSort = ({
    direction
}: {
    direction?: DataTableSortDirection;
}): TableSortDirection | undefined => {
    if(!direction) {
        return undefined;
    }

    return direction === "asc" ? "ascending" : "descending";
};

export const downloadDataTableCsv = ({
    fileName,
    csv
}: {
    fileName: string;
    csv: string;
}) => {
    const doc = Platform.OS === "web" ? globalThis.document : undefined;

    if(!doc || typeof Blob === "undefined") {
        warnOnce({
            message: "DataTable.Export: there is no file download on this platform. Pass \"onExport\" to receive the data."
        });

        return;
    }

    const url = URL.createObjectURL(new Blob([
        `${DATA_TABLE_CSV_BOM}${csv}`
    ], {
        type: "text/csv;charset=utf-8"
    }));

    const anchor = doc.createElement("a");

    anchor.href = url;

    anchor.download = `${fileName}.csv`;

    doc.body.appendChild(anchor);

    anchor.click();

    anchor.remove();

    URL.revokeObjectURL(url);
};

export const getDataTableCardRole = (column: IDataTableColumnBase) => {
    return column.cardRole ?? (column.isHeaderHidden ? "action" : "field");
};

export const getDataTableCardLayout = ({
    columns
}: {
    columns: Array<IDataTableColumnBase>;
}): DataTableCardLayoutType => {
    const titleColumn = columns.find((column) => getDataTableCardRole(column) === "title") ?? columns.find((column) => getDataTableCardRole(column) === "field");

    return {
        subtitles: columns.filter((column) => getDataTableCardRole(column) === "subtitle"),
        action: columns.find((column) => getDataTableCardRole(column) === "action"),
        fields: columns.filter((column) => column !== titleColumn && [
            "status",
            "amount",
            "field"
        ].includes(getDataTableCardRole(column))),
        title: titleColumn
    };
};

const DATA_TABLE_LISTBOX_ROLE: string = "listbox";

export const DATA_TABLE_LISTBOX_PROPS = {
    role: DATA_TABLE_LISTBOX_ROLE
} as Pick<ViewProps, "role">;
